// How much of Merlin's question list is noise (kanban 3709db93): the
// relevance rate against Ravi's bar (about 80% relevant is workable; below
// that people walk away), per run so a round of prompt changes can be
// judged, and per team so the pilot can be turned on for one audience at a
// time (Hadi: "choose based on the group that we are going to give it to
// first. Some people have a lot less tolerance"). Sits at the top of the
// Merlin's questions tab; the marks come from the Yes / No buttons on the cards.

import type { RelevanceBucket, RelevanceSummary } from '../../services/roadmapReports';
import { ratePct, rateTone } from './relevanceUtils';
import styles from './RoadmapV2.module.css';

interface Props {
  summary: RelevanceSummary | null;
}

function Rate({ b, target }: { b: RelevanceBucket; target: number }) {
  const tone = rateTone(b.rate, target);
  const pct = b.rate === null ? 0 : Math.round(b.rate * 100);
  return (
    <span className={styles.relevanceRate} data-tone={tone}>
      <span className={styles.relevanceBar} aria-hidden="true">
        <span className={styles.relevanceBarFill} style={{ width: `${pct}%` }} />
        <span className={styles.relevanceBarTarget} style={{ left: `${Math.round(target * 100)}%` }} />
      </span>
      <strong>{ratePct(b.rate)}</strong>
      <span className={styles.meta}>
        {b.relevant} yes · {b.not_relevant} no · {b.questions} question{b.questions === 1 ? '' : 's'}
      </span>
    </span>
  );
}

export function RelevancePanel({ summary }: Props) {
  if (!summary) return null;
  const { overall, by_run: byRun, by_team: byTeam, target_rate: target, open_questions: open, unmarked } = summary;
  const marked = open - unmarked;
  const tone = rateTone(overall.rate, target);
  return (
    <section className={`${styles.panel} ${styles.relevancePanel}`} aria-label="How relevant Merlin's questions are" data-testid="relevance-panel">
      <div className={styles.panelHead}>
        <h2>
          Relevance{' '}
          <span className={styles.relevanceHeadline} data-tone={tone} data-testid="relevance-overall">
            {ratePct(overall.rate)}
          </span>
        </h2>
        <span className={styles.meta}>
          {overall.relevant + overall.not_relevant === 0
            ? 'nobody has marked a question yet — use Yes / No on the cards below'
            : `${overall.relevant} relevant, ${overall.not_relevant} not, over ${overall.questions} question${overall.questions === 1 ? '' : 's'} · bar ${ratePct(target)}`}
          {open > 0 && (
            <> · this list: <span data-testid="relevance-coverage">{marked} of {open} marked</span></>
          )}
        </span>
      </div>
      {byRun.length > 0 && (
        <div className={styles.relevanceGrid}>
          <div>
            <h3 className={styles.relevanceSub}>By run</h3>
            <ul className={styles.relevanceList} data-testid="relevance-by-run">
              {byRun.map((b) => (
                <li key={b.report_id ?? b.label}>
                  <span className={styles.relevanceLabel}>{b.label}</span>
                  <Rate b={b} target={target} />
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className={styles.relevanceSub}>By team</h3>
            <ul className={styles.relevanceList} data-testid="relevance-by-team">
              {byTeam.map((b) => (
                <li key={b.label}>
                  <span className={styles.relevanceLabel}>{b.label}</span>
                  <Rate b={b} target={target} />
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </section>
  );
}
